import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Table } from '@components/Table';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import { RIGHTS } from '@constants/user';
import { CouponType, CouponStatus } from '@constants/coupons';
import { couponsActions } from '@actions';

const CroppedText = styled(Typography)({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
    '@media (max-width: 1024px)': {
        maxWidth: 200,
    },
});

const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.type')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const GetTableBody = (data, companies, brands, onClickAction) => {
    const { t } = useTranslation();
    const { hasAccessToAssetById } = useHasAccessToAsset();

    return data.map(coupon => {
        const locationName = coupon.locationId
            ? companies?.find(company => company.companyId === coupon.locationId)?.internalName
            : brands?.find(brand => brand.id === coupon.brandId)?.title;
        const rights = hasAccessToAssetById(coupon);
        const CouponTypeMap = {
            [CouponType.PercentageOff]: t('CouponsPage.percentageOff'),
            [CouponType.AmountOff]: t('CouponsPage.amountOff'),
            [CouponType.FreeItem]: t('CouponsPage.freeItem'),
        };

        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {coupon.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <CroppedText variant="body2" align="left">
                        {locationName || '--'}
                    </CroppedText>
                ),
            },
            {
                component: (
                    <Typography style={{ width: 220 }} variant="body2" align="left">
                        {CouponTypeMap[coupon.type]}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {rights[RIGHTS.WRITE] && (
                            <Tooltip
                                title={coupon.status === CouponStatus.Paused ? t('buttons.enable') : t('buttons.pause')}
                                placement="top"
                            >
                                <IconButton
                                    onClick={() =>
                                        onClickAction(
                                            coupon,
                                            coupon.status === CouponStatus.Paused ? 'resume' : 'pause',
                                        )
                                    }
                                >
                                    {coupon.status === CouponStatus.Paused ? (
                                        <PlayArrowIcon viewBox="0 0 22 22" />
                                    ) : (
                                        <PauseIcon viewBox="0 0 22 22" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={t('common.view')} placement="top">
                            <IconButton onClick={() => onClickAction(coupon, 'view')}>
                                <VisibilityIcon viewBox="0 0 22 22" />
                            </IconButton>
                        </Tooltip>
                        {rights[RIGHTS.WRITE] && (
                            <Tooltip title={t('common.edit')} placement="top">
                                <IconButton onClick={() => onClickAction(coupon, 'edit')}>
                                    <EditIcon viewBox="0 0 22 22" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {rights[RIGHTS.WRITE] && (
                            <Tooltip title={t('common.delete')} placement="top">
                                <IconButton onClick={() => onClickAction(coupon, 'archive')}>
                                    <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Typography>
                ),
            },
        ];
    });
};

const CouponsTable = ({ setPage, page, setRowsPerPage, rowsPerPage, onClickCoupon, companies, brands }) => {
    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.coupons);

    const dispatch = useDispatch();

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(couponsActions.get(newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(couponsActions.get(0, parseInt(event.target.value, 10)));
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={GetTableHeader()}
                rows={GetTableBody(data, companies, brands, onClickCoupon)}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 5,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    labelDisplayedRows: ({ from, to, count }) =>
                        `${from}–${to} ${t('Table.of')} ${count !== -1 ? count : `${t('Table.moreThan')} ${to}`}`,
                    labelRowsPerPage: <span>{t('Table.rowsPerPage')}</span>,
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default CouponsTable;
